"use client";

import { ChevronDown } from "lucide-react";
import { useEffect, useId, useRef, useState, type CSSProperties } from "react";

import { CcTrafficSliderField } from "@/components/services/seo/CcTrafficSliderField";
import {
  formatWholeNumber,
  parsePercent,
  parseWholeNumber,
  type RevenueField,
  calculatorCurrency,
  moneyRange,
} from "@/components/services/seo/seo-revenue-projection";
import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";

import type { AdvertisingDemoCopy } from "./advertising-demo-copy";
import { AD_BUDGET_STEPS, AD_CALCULATOR_RANGES, computeAdResult } from "./ad-calculator";

type CcAdCalculatorProps = {
  copy: AdvertisingDemoCopy["calculator"];
  locale: string;
};

/**
 * "Vad kan annonsering ge er?": budget, cost per click, conversion, close
 * rate and order value → visitors, enquiries, customers, revenue and the
 * return per advertising krona, plus revenue at a few budget levels. Same
 * layout and styles as the SEO and lead generation calculators.
 */
export function CcAdCalculator({ copy, locale }: CcAdCalculatorProps) {
  const ids = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const ranges = {
    ...AD_CALCULATOR_RANGES,
    budget: moneyRange(AD_CALCULATOR_RANGES.budget, locale),
    cpc: moneyRange(AD_CALCULATOR_RANGES.cpc, locale),
    value: moneyRange(AD_CALCULATOR_RANGES.value, locale),
  };
  const money = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: calculatorCurrency(locale),
    maximumFractionDigits: 0,
  });

  const [budget, setBudget] = useState(formatWholeNumber(ranges.budget.fallback, locale));
  const [cpc, setCpc] = useState(String(ranges.cpc.fallback));
  const [conversion, setConversion] = useState(String(ranges.conversion.fallback));
  const [close, setClose] = useState(String(ranges.close.fallback));
  const [value, setValue] = useState(formatWholeNumber(ranges.value.fallback, locale));
  const [howOpen, setHowOpen] = useState(false);
  const resultRef = useRef<HTMLDivElement | null>(null);

  // Bars grow in once the results are on screen (same hook as the SEO calculator).
  useEffect(() => {
    const node = resultRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        node.dataset.inview = "true";
      },
      { threshold: 0.28 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const budgetNumber = parseWholeNumber(budget) ?? 0;
  const result = computeAdResult({
    budget: budgetNumber,
    cpc: parseWholeNumber(cpc) ?? 0,
    conversionPercent: parsePercent(conversion) ?? 0,
    closePercent: parsePercent(close) ?? 0,
    value: parseWholeNumber(value) ?? 0,
  });
  const max = Math.max(1, ...result.revenueByBudget);
  const ratio = new Intl.NumberFormat(locale, { maximumFractionDigits: 1, minimumFractionDigits: 1 });

  // The slider field styles itself per field name; these map onto the SEO calculator's.
  const slider = (
    field: RevenueField,
    key: keyof typeof ranges,
    label: string,
    raw: string,
    onChange: (next: string) => void,
    kind: "whole" | "percent",
    unit?: string,
  ) => (
    <CcTrafficSliderField
      id={`${ids}-${key}`}
      field={field}
      label={label}
      raw={raw}
      onChange={onChange}
      kind={kind}
      range={ranges[key]}
      locale={locale}
      unit={unit ?? (kind === "percent" ? "%" : undefined)}
    />
  );

  return (
    <section
      data-analytics-tool="ads_calculator"
      className="cc-section cc-lead-calculator cc-ad-calculator"
      aria-labelledby={`${ids}-title`}
      data-cc-nav="light"
      {...devSectionAttr("ad-calculator")}
    >
      <div className="cc-container">
        <div className="cc-traffic" data-traffic-calculator-version={2}>
          <header className="cc-traffic__header">
            <CcSanjayaEyebrow theme="on-subtle">{copy.eyebrow}</CcSanjayaEyebrow>
            <h2 id={`${ids}-title`} className="cc-traffic__title">
              {copy.title}
            </h2>
            <p className="cc-traffic__text">{copy.text}</p>
          </header>

          <div className="cc-traffic__columns">
            <div className="cc-traffic__side">
              <div className="cc-traffic__stack">
                <div className="cc-traffic__controls">
                  {slider("visits", "budget", copy.budgetLabel, budget, setBudget, "whole", copy.budgetUnit)}
                  {slider("value", "cpc", copy.cpcLabel, cpc, setCpc, "whole", copy.cpcUnit)}
                  {slider("conversion", "conversion", copy.conversionLabel, conversion, setConversion, "percent")}
                  {slider("close", "close", copy.closeLabel, close, setClose, "percent")}
                  {slider("growth", "value", copy.valueLabel, value, setValue, "whole", copy.valueUnit)}
                </div>
                <div className="cc-traffic__cta cc-lead-calculator__cta">
                  <CcButton
                    bookCallPopup
                    bookCallVariant="default"
                    label={copy.cta}
                    expand
                    expandCompact
                    className="cc-traffic__button"
                  />
                </div>
              </div>
            </div>

            <div className="cc-traffic__panel cc-traffic__panel--results">
              <div
                ref={resultRef}
                className="cc-traffic__result cc-traffic__result--impact-v2"
                aria-live="polite"
              >
                <p className="cc-traffic__impact-eyebrow">{copy.impactEyebrow}</p>
                <div className="cc-traffic__impact-hero">
                  <p className="cc-traffic__impact-hero-sub">{copy.heroLabel}</p>
                  <p className="cc-traffic__impact-hero-value">
                    {`${ratio.format(result.returnPerKrona)} ${copy.valueUnit}`}
                  </p>
                  <p className="cc-traffic__impact-hero-secondary">{copy.heroSub}</p>
                </div>
                <dl className="cc-traffic__impact-stats">
                  <div className="cc-traffic__impact-stat">
                    <dd className="cc-traffic__impact-stat-value">
                      {formatWholeNumber(Math.round(result.leads), locale)}
                    </dd>
                    <dt className="cc-traffic__impact-stat-label">{copy.leadsLabel}</dt>
                  </div>
                  <div className="cc-traffic__impact-stat">
                    <dd className="cc-traffic__impact-stat-value">
                      {formatWholeNumber(Math.round(result.customers), locale)}
                    </dd>
                    <dt className="cc-traffic__impact-stat-label">{copy.customersLabel}</dt>
                  </div>
                  <div className="cc-traffic__impact-stat">
                    <dd className="cc-traffic__impact-stat-value">
                      {money.format(Math.round(result.costPerCustomer))}
                    </dd>
                    <dt className="cc-traffic__impact-stat-label">{copy.cpaLabel}</dt>
                  </div>
                </dl>

                <p className="cc-traffic__chart-title cc-traffic__chart-title--impact">
                  {copy.chartTitle}
                </p>
                <ol className="cc-traffic__bars">
                  {result.revenueByBudget.map((amount, index) => (
                    <li key={index} className="cc-traffic__bar">
                      <span className="cc-traffic__bar-track">
                        <span className="cc-traffic__bar-value">
                          {money.format(Math.round(amount))}
                        </span>
                        <span
                          className="cc-traffic__bar-fill"
                          aria-hidden="true"
                          style={{ "--cc-bar": Math.max(0.02, amount / max) } as CSSProperties}
                        />
                      </span>
                      <span className="cc-traffic__bar-label">
                        {money.format(Math.round(budgetNumber * AD_BUDGET_STEPS[index]))}
                      </span>
                    </li>
                  ))}
                </ol>

                <p className="cc-traffic__help">{copy.footnote}</p>

                <div className="cc-traffic__how">
                  <button
                    type="button"
                    className="cc-traffic__toggle"
                    aria-expanded={howOpen}
                    aria-controls={`${ids}-how`}
                    onClick={() => setHowOpen((open) => !open)}
                  >
                    {copy.howLink}
                    <ChevronDown size={14} strokeWidth={2} aria-hidden />
                  </button>
                  <div
                    id={`${ids}-how`}
                    className="cc-traffic__how-body"
                    data-open={howOpen ? "true" : "false"}
                    aria-hidden={!howOpen}
                  >
                    <div className="cc-traffic__how-collapse">
                      <div className="cc-traffic__how-content" inert={howOpen ? undefined : true}>
                        {copy.how.map((line) => (
                          <p key={line}>{line}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
